import React, { useState, useRef, useEffect } from "react";
import { Send, Search, X, User } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton, AppModal } from "../ui";
import { ASSIGNEE_TYPES } from "./assetConstants";
import "./assets.scss";

/**
 * AssignAssetModal — assigns an asset to admin/instructor/student/branch/other.
 *
 * Props:
 *   show, onHide, assetId, onAssigned()
 */

const PERSON_TYPES = ["student", "instructor", "admin"];

const blankForm = {
  assigneeType: "student",
  assigneeId: "",
  assigneeName: "",
  expectedReturnAt: "",
  conditionOnIssue: "good",
  notes: "",
};

const AssignAssetModal = ({ show, onHide, assetId, onAssigned }) => {
  const [form, setForm] = useState(blankForm);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Reset on open
  useEffect(() => {
    if (show) {
      setForm(blankForm);
      setQuery("");
      setResults([]);
      setSelectedPerson(null);
      setError("");
    }
  }, [show]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleTypeChange = (type) => {
    setForm(prev => ({ ...prev, assigneeType: type, assigneeId: "", assigneeName: "" }));
    setSelectedPerson(null);
    setQuery("");
    setResults([]);
  };

  const handleSearch = (value) => {
    setQuery(value);
    setSelectedPerson(null);
    setForm(prev => ({ ...prev, assigneeId: "", assigneeName: "" }));
    if (value.trim().length < 3) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await apiClient.get(
          `/assets/search-assignees?type=${form.assigneeType}&q=${encodeURIComponent(value.trim())}`
        );
        setResults(data.results || []);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 350);
  };

  const selectPerson = (person) => {
    setSelectedPerson(person);
    setQuery("");
    setResults([]);
    setForm(prev => ({ ...prev, assigneeId: person._id, assigneeName: person.name }));
  };

  const clearPerson = () => {
    setSelectedPerson(null);
    setQuery("");
    setResults([]);
    setForm(prev => ({ ...prev, assigneeId: "", assigneeName: "" }));
  };

  const handleSubmit = async () => {
    setError("");
    const isPerson = PERSON_TYPES.includes(form.assigneeType);
    if (isPerson && !form.assigneeId) {
      setError("Please select an assignee from the search results.");
      return;
    }
    if (!isPerson && !form.assigneeName.trim()) {
      setError("Assignee name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/assets/${assetId}/assign`, {
        ...form,
        expectedReturnAt: form.expectedReturnAt || null,
      });
      onAssigned?.();
      onHide();
    } catch (err) {
      setError(err?.response?.data?.message || "Assignment failed.");
    }
    setSubmitting(false);
  };

  const isPerson = PERSON_TYPES.includes(form.assigneeType);

  return (
    <AppModal show={show} onHide={onHide} title="Assign Asset" size="md">
      <div>
        {/* Assignee Type */}
        <div className="assets-modal__field">
          <label className="assets-modal__label">Assignee Type *</label>
          <select
            className="assets-modal__select"
            value={form.assigneeType}
            onChange={e => handleTypeChange(e.target.value)}
          >
            {ASSIGNEE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Smart search for student / instructor / admin */}
        {isPerson ? (
          <div className="assets-modal__field">
            <label className="assets-modal__label">Assignee *</label>
            {selectedPerson ? (
              <div className="assign-selected-person">
                <User size={14} className="assign-selected-person__icon" />
                <div className="assign-selected-person__info">
                  <span className="assign-selected-person__name">{selectedPerson.name}</span>
                  {selectedPerson.email && (
                    <span className="assign-selected-person__sub">{selectedPerson.email}</span>
                  )}
                </div>
                <button type="button" className="assign-selected-person__clear" onClick={clearPerson}>
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div className="assign-search-wrap" ref={dropdownRef}>
                <div className="assign-search-input-wrap">
                  <Search size={14} className="assign-search-icon" />
                  <input
                    className="assets-modal__input assign-search-input"
                    value={query}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder={`Type 3+ chars to search ${form.assigneeType}s…`}
                  />
                  {searching && <span className="assign-search-spinner" />}
                </div>
                {results.length > 0 && (
                  <ul className="assign-search-dropdown">
                    {results.map(r => (
                      <li
                        key={r._id}
                        className="assign-search-dropdown__item"
                        onMouseDown={() => selectPerson(r)}
                      >
                        <User size={13} className="assign-search-dropdown__avatar" />
                        <div className="assign-search-dropdown__text">
                          <span className="assign-search-dropdown__name">{r.name}</span>
                          {r.email && <span className="assign-search-dropdown__sub">{r.email}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {query.trim().length >= 3 && !searching && results.length === 0 && (
                  <div className="assign-search-empty">No {form.assigneeType}s found</div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Plain name input for branch / room */
          <div className="assets-modal__field">
            <label className="assets-modal__label">Assignee Name *</label>
            <input
              className="assets-modal__input"
              value={form.assigneeName}
              onChange={e => update("assigneeName", e.target.value)}
              placeholder="Branch / Room name"
            />
          </div>
        )}

        {/* Expected return */}
        <div className="assets-modal__field">
          <label className="assets-modal__label">Expected Return Date</label>
          <input
            type="date"
            className="assets-modal__input"
            value={form.expectedReturnAt}
            onChange={e => update("expectedReturnAt", e.target.value)}
          />
        </div>

        {/* Condition */}
        <div className="assets-modal__field">
          <label className="assets-modal__label">Condition on Issue</label>
          <select
            className="assets-modal__select"
            value={form.conditionOnIssue}
            onChange={e => update("conditionOnIssue", e.target.value)}
          >
            <option value="new">New</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
            <option value="poor">Poor</option>
          </select>
        </div>

        {/* Notes */}
        <div className="assets-modal__field">
          <label className="assets-modal__label">Notes</label>
          <textarea
            className="assets-modal__textarea"
            value={form.notes}
            onChange={e => update("notes", e.target.value)}
            rows={3}
          />
        </div>

        {error && <div className="assets-modal__error" role="alert">{error}</div>}

        <div className="asset-form__actions">
          <AppButton variant="secondary" onClick={onHide} disabled={submitting}>Cancel</AppButton>
          <AppButton onClick={handleSubmit} disabled={submitting}>
            <Send size={14} /> {submitting ? "Assigning…" : "Assign"}
          </AppButton>
        </div>
      </div>
    </AppModal>
  );
};

export default AssignAssetModal;
